"use client";

import { datadogRum } from "@datadog/browser-rum";
import { createContext, PropsWithChildren, useContext, useEffect } from "react";
import { useAuth } from "../AuthenticationProvider";

type DataDogProviderContext = {
  captureException: (
    error: Error,
    extra?: unknown,
    tags?: Record<string, string>
  ) => void;
};

const Context = createContext<DataDogProviderContext>({
  captureException: () => undefined,
});

export const DATADOG_ENABLED =
  process.env.NEXT_PUBLIC_ENVIRONMENT === "production" ||
  process.env.NEXT_PUBLIC_ENVIRONMENT === "staging";

export function DatadogProvider({ children }: PropsWithChildren<unknown>) {
  const { user } = useAuth();

  useEffect(() => {
    if (!DATADOG_ENABLED) {
      console.info(
        `DataDog disabled on environment "${process.env.NEXT_PUBLIC_ENVIRONMENT}"`
      );

      return;
    }

    if (
      !process.env.NEXT_PUBLIC_DD_APPLICATION_ID ||
      !process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN
    ) {
      console.info(
        `DataDog NEXT_PUBLIC_DD_APPLICATION_ID and NEXT_PUBLIC_DD_CLIENT_TOKEN not setup`
      );

      return;
    }

    datadogRum.init({
      applicationId: process.env.NEXT_PUBLIC_DD_APPLICATION_ID,
      clientToken: process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN,
      site: "datadoghq.com",
      service: "pwa",
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      defaultPrivacyLevel: "allow",
      env: process.env.NEXT_PUBLIC_ENVIRONMENT,
      version: process.env.NEXT_PUBLIC_VERSION,
      allowedTracingUrls: [
        {
          match: /https:\/\/.*\.bigspring\.io\/app\/graphql/,
          propagatorTypes: ["tracecontext"],
        },
      ],
    });
  }, []);

  useEffect(() => {
    if (!DATADOG_ENABLED) {
      return;
    }

    if (!user) {
      datadogRum.clearUser();
    } else {
      datadogRum.setUser({
        id: user.id,
        email: user.emailAddress || undefined,
        name: user.externalId || undefined,
        currentCompanyId: user.company.id,
      });
    }
  }, [user]);

  const captureException = (
    error: Error,
    extra?: unknown,
    tags?: Record<string, string>
  ) => {
    if (DATADOG_ENABLED) {
      try {
        datadogRum.addError(error, { extra, tags });
      } catch (exception) {
        console.error("Error requesting to Datadog: ", exception);
      }
    } else {
      if (error && error.message) {
        console.group("Datadog Disabled - Error:", error.message);
      } else {
        console.group("Datadog Disabled - Error");
      }
      console.groupCollapsed("Details");
      console.info(error);
      console.groupEnd();
      if (extra) {
        console.groupCollapsed("Extra");
        console.info(extra);
        console.groupEnd();
      }
      if (tags) {
        console.groupCollapsed("Custom Tags");
        console.info(tags);
        console.groupEnd();
      }
      console.groupEnd();
    }
  };

  return (
    <Context.Provider value={{ captureException }}>{children}</Context.Provider>
  );
}

export function useDataDog() {
  return useContext(Context);
}
